import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useMemo, useState } from 'react';
import { ICategory } from 'shared/interfaces/ITalent';
import { arraysEqual } from 'shared/utils/common';
import { captureMessage } from '../../../../lib/sentry';
import { BasicDragDropComponents } from '../components/BasicDragDropComponents';
import {
  DragError,
  DragInfo,
  DragResult,
  StyleUtilOptions,
  UseCategoryDragDropOptions,
  UseCategoryDragDropReturn,
  ValidationResult,
} from './useCategoryDragDrop.types';

/**
 * Custom hook for handling drag and drop functionality for ICategory[] structure
 * Provides data manipulation, validation, error handling, and basic components
 */
export const useCategoryDragDrop = (
  initialCategories: ICategory[],
  options: UseCategoryDragDropOptions = {},
): UseCategoryDragDropReturn => {
  const { enableCrossCategoryMoves = true, enableValidation = true, onError, onDataChange } = options;

  // State management
  const [categories, setCategories] = useState<ICategory[]>(initialCategories);
  const [dragError, setDragError] = useState<DragError | null>(null);
  const [dragInfo, setDragInfo] = useState<DragInfo>({
    isDragging: false,
  });

  // Validation functions
  const validateDragOperation = useCallback(
    (result: DragResult): ValidationResult => {
      const { type, source, destination } = result;

      // Basic validation - no destination
      if (!destination) {
        return { isValid: true }; // Valid to cancel drag
      }

      // No change validation
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return { isValid: true }; // Valid no-op
      }

      try {
        // Category reordering validation
        if (type === 'CATEGORY') {
          if (source.index >= categories.length || source.index < 0) {
            return {
              isValid: false,
              error: {
                type: 'validation',
                message: 'Invalid source category index',
                context: { sourceIndex: source.index, categoriesLength: categories.length },
                userMessage: 'Invalid category position. Please refresh the page and try again.',
              },
            };
          }

          if (destination.index > categories.length || destination.index < 0) {
            return {
              isValid: false,
              error: {
                type: 'validation',
                message: 'Invalid destination category index',
                context: { destinationIndex: destination.index, categoriesLength: categories.length },
                userMessage: 'Invalid drop position. Please try again.',
              },
            };
          }

          return { isValid: true };
        }

        // Credit reordering validation
        if (type === 'CREDIT') {
          const sourceCategoryId = source.droppableId;
          const destinationCategoryId = destination.droppableId;

          // Find source and destination categories
          const sourceCategory = categories.find((cat) => cat.category_id === sourceCategoryId);
          const destinationCategory = categories.find((cat) => cat.category_id === destinationCategoryId);

          if (!sourceCategory) {
            return {
              isValid: false,
              error: {
                type: 'validation',
                message: 'Source category not found',
                context: { sourceCategoryId, availableCategories: categories.map((c) => c.category_id) },
                userMessage: 'Invalid source category. Please refresh the page and try again.',
              },
            };
          }

          if (!destinationCategory) {
            return {
              isValid: false,
              error: {
                type: 'validation',
                message: 'Destination category not found',
                context: { destinationCategoryId, availableCategories: categories.map((c) => c.category_id) },
                userMessage: 'Invalid destination category. Please refresh the page and try again.',
              },
            };
          }

          // Validate source index
          if (source.index >= sourceCategory.credits.length || source.index < 0) {
            return {
              isValid: false,
              error: {
                type: 'validation',
                message: 'Invalid source credit index',
                context: { sourceIndex: source.index, creditsLength: sourceCategory.credits.length },
                userMessage: 'Invalid item position. Please refresh the page and try again.',
              },
            };
          }

          // Cross-category move validation
          if (sourceCategoryId !== destinationCategoryId && !enableCrossCategoryMoves) {
            return {
              isValid: false,
              error: {
                type: 'operation',
                message: 'Cross-category moves are disabled',
                context: { sourceCategoryId, destinationCategoryId },
                userMessage: 'Moving items between categories is not allowed.',
              },
            };
          }

          return { isValid: true };
        }

        // Unknown type validation
        return {
          isValid: false,
          error: {
            type: 'validation',
            message: 'Unknown drag type',
            context: { type },
            userMessage: 'Unknown drag operation. Please try again.',
          },
        };
      } catch (error) {
        return {
          isValid: false,
          error: {
            type: 'validation',
            message: 'Validation error occurred',
            context: { error, result },
            userMessage: 'An error occurred during validation. Please try again.',
          },
        };
      }
    },
    [categories, enableCrossCategoryMoves],
  );

  // Data manipulation functions
  const handleCategoryReorder = useCallback(
    (source: DragResult['source'], destination: DragResult['destination']) => {
      if (!destination) return categories;

      const reorderedCategories = arrayMove(categories, source.index, destination.index);

      // Data integrity check
      if (arraysEqual(reorderedCategories, categories)) {
        return categories; // No change needed
      }

      return reorderedCategories;
    },
    [categories],
  );

  const handleCreditReorder = useCallback(
    (source: DragResult['source'], destination: DragResult['destination']) => {
      if (!destination) return categories;

      const sourceCategoryId = source.droppableId;
      const destinationCategoryId = destination.droppableId;

      // Same category reordering
      if (sourceCategoryId === destinationCategoryId) {
        const categoryIndex = categories.findIndex((cat) => cat.category_id === sourceCategoryId);
        const category = categories[categoryIndex];
        const reorderedCredits = arrayMove(category.credits, source.index, destination.index);

        if (arraysEqual(reorderedCredits, category.credits)) {
          return categories; // No change needed
        }

        const updatedCategories = categories.map((cat, index) =>
          index === categoryIndex ? { ...cat, credits: reorderedCredits } : cat,
        );

        return updatedCategories;
      }

      // Cross-category movement
      const sourceCategoryIndex = categories.findIndex((cat) => cat.category_id === sourceCategoryId);
      const destinationCategoryIndex = categories.findIndex((cat) => cat.category_id === destinationCategoryId);

      const sourceCategory = { ...categories[sourceCategoryIndex] };
      const destinationCategory = { ...categories[destinationCategoryIndex] };

      // Remove credit from source
      const sourceCredits = [...sourceCategory.credits];
      const [movedCredit] = sourceCredits.splice(source.index, 1);

      if (!movedCredit) {
        throw new Error('Could not find the item to move');
      }

      // Add credit to destination
      const destinationCredits = [...destinationCategory.credits];
      destinationCredits.splice(destination.index, 0, movedCredit);

      // Update categories
      const updatedCategories = categories.map((cat, index) => {
        if (index === sourceCategoryIndex) {
          return { ...cat, credits: sourceCredits };
        }
        if (index === destinationCategoryIndex) {
          return { ...cat, credits: destinationCredits };
        }
        return cat;
      });

      // Log successful cross-category move
      captureMessage({
        message: 'Successful cross-category move',
        data: {
          movedCredit: movedCredit.title || movedCredit.role,
          fromCategory: sourceCategory.category,
          toCategory: destinationCategory.category,
        },
      });

      return updatedCategories;
    },
    [categories],
  );

  // Error handling
  const handleError = useCallback(
    (error: DragError) => {
      setDragError(error);

      // Log error for debugging
      captureMessage({
        message: `Drag operation error: ${error.type}`,
        data: { error },
      });

      // Call external error handler if provided
      if (onError) {
        onError(error);
      }
    },
    [onError],
  );

  const resetError = useCallback(() => {
    setDragError(null);
  }, []);

  // Main drag handler
  const onDragEnd = useCallback(
    (result: DragResult) => {
      const { type, source, destination } = result;

      // Update drag state
      setDragInfo({
        isDragging: false,
        draggedItemId: undefined,
        draggedItemType: undefined,
        sourceId: undefined,
        destinationId: undefined,
      });

      // No destination - drag was cancelled
      if (!destination) {
        return;
      }

      // No change - early return
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      try {
        // Clear previous errors
        resetError();

        // Validate operation if enabled
        if (enableValidation) {
          const validation = validateDragOperation(result);
          if (!validation.isValid && validation.error) {
            handleError(validation.error);
            return;
          }
        }

        let updatedCategories: ICategory[];

        // Handle different drag types
        if (type === 'CATEGORY') {
          updatedCategories = handleCategoryReorder(source, destination);
        } else if (type === 'CREDIT') {
          updatedCategories = handleCreditReorder(source, destination);
        } else {
          throw new Error(`Unknown drag type: ${type}`);
        }

        // Update state and notify parent
        setCategories(updatedCategories);
        if (onDataChange) {
          onDataChange(updatedCategories);
        }
      } catch (error) {
        const dragError: DragError = {
          type: 'operation',
          message: error instanceof Error ? error.message : 'Drag operation failed',
          context: { error, result },
          userMessage: 'Failed to move item. Please try again.',
        };
        handleError(dragError);
      }
    },
    [
      enableValidation,
      validateDragOperation,
      handleCategoryReorder,
      handleCreditReorder,
      handleError,
      resetError,
      onDataChange,
    ],
  );

  // Manual update function
  const updateCategories = useCallback(
    (newCategories: ICategory[]) => {
      setCategories(newCategories);
      if (onDataChange) {
        onDataChange(newCategories);
      }
    },
    [onDataChange],
  );

  // Direct state update function (for data synchronization - doesn't trigger onDataChange)
  const setCategoriesDirectly = useCallback((newCategories: ICategory[]) => {
    setCategories(newCategories);
  }, []);

  // Style utility functions
  const getDroppableStyle = useCallback((options: StyleUtilOptions) => {
    const { isDraggingOver = false, theme = 'light', customStyles = {} } = options;
    const baseStyle = {
      flex: 1,
      transition: 'all 300ms ease',
      borderRadius: 8,
      minHeight: isDraggingOver ? '100px' : 'auto',
      position: 'relative' as const,
    };

    return { ...baseStyle, ...customStyles };
  }, []);

  const getDraggableStyle = useCallback((options: StyleUtilOptions) => {
    const { isDragging = false, customStyles = {} } = options;
    const baseStyle = {
      borderRadius: 8,
      display: 'flex',
      userSelect: 'none' as const,
      boxShadow: isDragging ? '0 4px 8px rgba(0,0,0,0.15)' : 'none',
      opacity: isDragging ? 0.9 : 1,
    };
    return { ...baseStyle, ...customStyles };
  }, []);

  // Memoized basic components
  const basicComponents = useMemo(() => {
    return BasicDragDropComponents;
  }, []);

  return {
    // Data
    categories,

    // Handlers
    onDragEnd,
    updateCategories,
    setCategoriesDirectly,
    resetError,

    // State
    isDragging: dragInfo.isDragging,
    dragError,
    dragInfo,

    // Basic Components
    DragDropProvider: basicComponents.DragDropProvider,
    CategoryDroppable: basicComponents.CategoryDroppable,
    CategoryDraggable: basicComponents.CategoryDraggable,

    // Style utilities
    getDroppableStyle,
    getDraggableStyle,
  };
};
